import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PenLine, X, Globe, Save, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { loadOverrides, normPath, originalOf } from "../lib/siteCopy";
import { MediaPicker } from "./MediaPicker";

const KEY = "dk_copy_edit";

// Live "edit this page" mode for staff with content.edit: click any text or image on the public site to change it.
export default function CopyEditMode() {
  const { user, can } = useAuth();
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const [on, setOn] = useState(() => localStorage.getItem(KEY) === "1");
  const [target, setTarget] = useState(null); // { kind, nodes:[{node, original}], img }
  const allowed = !!user && (can("content.edit") || can("content.create"));

  useEffect(() => { if (params.get("dkEdit") === "1" && allowed) { localStorage.setItem(KEY, "1"); setOn(true); params.delete("dkEdit"); setParams(params, { replace: true }); } }, [params, allowed, setParams]);
  useEffect(() => {
    if (!on || !allowed) { document.body.classList.remove("dk-copy-edit"); return; }
    document.body.classList.add("dk-copy-edit");
    const onClick = (e) => {
      if (e.target.closest("[data-copy-editor]")) return;
      const img = e.target.closest("img");
      let el = img || e.target;
      e.preventDefault(); e.stopPropagation();
      if (img) { setTarget({ kind: "image", img, original: originalOf(img) }); return; }
      for (let i = 0; i < 4 && el; i++, el = el.parentElement) {
        const nodes = [...el.childNodes].filter((n) => n.nodeType === 3 && n.nodeValue.trim().length > 1).map((n) => ({ node: n, original: originalOf(n), value: n.nodeValue.trim() }));
        if (nodes.length) { setTarget({ kind: "text", nodes, tag: el.tagName, chrome: !!el.closest("header, footer, nav") }); return; }
      }
      toast.message("Click directly on a piece of text or an image.");
    };
    document.addEventListener("click", onClick, true);
    return () => { document.removeEventListener("click", onClick, true); document.body.classList.remove("dk-copy-edit"); };
  }, [on, allowed]);

  if (!allowed) return null;
  const stop = () => { localStorage.removeItem(KEY); setOn(false); setTarget(null); };
  if (!on) return <button onClick={() => { localStorage.setItem(KEY, "1"); setOn(true); }} className="fixed bottom-24 left-4 z-[70] bg-[#155e3f] text-[#d6aa43] border border-[#d6aa43]/40 rounded-full px-4 py-2 text-xs font-bold shadow-lg inline-flex items-center gap-2" data-copy-editor data-testid="copy-edit-start"><PenLine size={14} /> Edit page text</button>;
  return (
    <>
      <div className="fixed top-0 inset-x-0 z-[80] bg-[#d6aa43] text-[#0d1724] text-xs font-bold px-4 py-2 flex flex-wrap items-center gap-3" data-copy-editor data-testid="copy-edit-bar">
        <PenLine size={14} /> Editing page text & images on <code>{normPath(pathname)}</code> — click any text or image. {user.role === "super_admin" ? "Your changes publish immediately." : "Your changes are saved as drafts for a publisher to approve."}
        <a href="/admin/site-copy" className="underline ml-auto">Manage all edits</a>
        <button onClick={stop} className="inline-flex items-center gap-1 bg-[#155e3f] text-white rounded-full px-3 py-1" data-testid="copy-edit-stop"><X size={12} /> Exit edit mode</button>
      </div>
      {target && <CopyEditorPanel target={target} path={normPath(pathname)} isSuper={user.role === "super_admin"} onClose={() => setTarget(null)} />}
    </>
  );
}

function CopyEditorPanel({ target, path, isSuper, onClose }) {
  const [values, setValues] = useState(() => (target.kind === "text" ? target.nodes.map((n) => n.value) : [""]));
  const [global, setGlobal] = useState(!!target.chrome);
  const [busy, setBusy] = useState(false);
  const save = async (publish) => {
    setBusy(true);
    try {
      const p = global ? "*" : path;
      if (target.kind === "image") {
        if (!values[0]) throw new Error("Choose a replacement image first.");
        await api.post("/admin/site-copy", { path: p, kind: "image", original: target.original, value: values[0], publish });
      } else {
        for (let i = 0; i < target.nodes.length; i++) if (values[i].trim() && values[i].trim() !== target.nodes[i].value) await api.post("/admin/site-copy", { path: p, kind: "text", original: target.nodes[i].original, value: values[i].trim(), publish });
      }
      await loadOverrides(true);
      toast.success(publish && isSuper ? "Published — live now." : "Saved as draft. A publisher can approve it in Admin → Site Content.");
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-y-0 right-0 z-[90] w-full max-w-md bg-white shadow-2xl border-l border-black/10 p-5 overflow-y-auto text-[#0a0d12]" data-copy-editor data-testid="copy-editor-panel">
      <div className="flex items-center justify-between"><h2 className="font-serif text-xl font-bold">{target.kind === "image" ? "Replace image" : "Edit text"}</h2><button onClick={onClose} aria-label="Close" data-testid="copy-editor-close"><X size={18} /></button></div>
      {target.kind === "text" ? target.nodes.map((n, i) => (
        <div key={`${n.original.slice(0, 40)}-${i}`} className="mt-4">
          <p className="text-[11px] uppercase tracking-wider text-[#0a0d12]/50">Original</p>
          <p className="text-xs bg-black/5 rounded p-2 mt-1 whitespace-pre-wrap">{n.original}</p>
          <label className="block text-xs font-semibold mt-2">New text</label>
          <textarea value={values[i]} onChange={(e) => setValues(values.map((v, k) => (k === i ? e.target.value : v)))} rows={Math.min(8, Math.max(2, Math.ceil(values[i].length / 50)))} className="w-full border rounded-lg p-2 text-sm mt-1" data-testid={`copy-editor-text-${i}`} />
        </div>)) : (
        <div className="mt-4 space-y-3">
          <p className="text-[11px] uppercase tracking-wider text-[#0a0d12]/50">Current image</p>
          <img src={target.img.src} alt="" className="w-full h-40 object-cover rounded-lg" />
          <MediaPicker value={values[0]} onChange={(v) => setValues([v])} label="Choose replacement" testid="copy-editor-image" />
        </div>)}
      <label className="flex items-center gap-2 text-xs mt-4"><input type="checkbox" checked={global} onChange={(e) => setGlobal(e.target.checked)} className="h-4 w-4" data-testid="copy-editor-global" /><Globe size={12} /> Apply everywhere this exact {target.kind} appears (all pages)</label>
      {target.chrome && <p className="text-[11px] text-[#7a5a0f] mt-1">Menu and footer labels appear on every page, so "apply everywhere" is pre-selected.</p>}
      <div className="flex flex-wrap gap-2 mt-5">
        {isSuper ? <button onClick={() => save(true)} disabled={busy} className="dk-btn-primary" data-testid="copy-editor-publish"><Save size={14} /> {busy ? "Saving…" : "Publish now"}</button>
          : <button onClick={() => save(false)} disabled={busy} className="dk-btn-primary" data-testid="copy-editor-save"><Send size={14} /> {busy ? "Saving…" : "Save draft for approval"}</button>}
        {isSuper && <button onClick={() => save(false)} disabled={busy} className="dk-btn-outline !text-[#0d1724] !border-[#0d1724]" data-testid="copy-editor-draft">Save as draft</button>}
      </div>
      <p className="text-[11px] text-[#0a0d12]/55 mt-4">Edits are matched to the original wording, so the same sentence elsewhere on this page changes too. Restore the original any time from Admin → Site Content.</p>
    </div>
  );
}
